import React from 'react';
import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Form, Input, Upload, Tooltip, Row, Col, message } from 'antd';
import { UploadFile } from 'antd/es/upload/interface';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { blogAdd } from '../../../utils/API';

interface FormValues {
  title: string;
  description: string;
  content: {
    section: string;
    description: string;
    image: UploadFile[];
  }[];
}


const AddBlog: React.FC = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();


  const onFinish = async (values: FormValues) => {
    // console.log('Received values of form: ', values);
  
    try {
      const formData = new FormData();
  
      // Append simple fields (title and description)
      formData.append('title', values.title);
      formData.append('description', values.description);
  
      // Append nested fields for each content item, including images
      if (Array.isArray(values.content)) {
        values.content.forEach((contentItem, index) => {
          formData.append(`content[${index}][section]`, contentItem.section);
          formData.append(`content[${index}][description]`, contentItem.description);
  
          // Append the first file in fileList as image, assuming a single file is uploaded
          if (contentItem.image && contentItem.image[0] && contentItem.image[0].originFileObj) {
            formData.append(`content[${index}][image]`, contentItem.image[0].originFileObj);
          }
        });
      }
  
      const response = await blogAdd(formData);
      if (response.data.message) {
        message.success(response.data.message);
        navigate("/backend/blog");
      }
    } catch (error) {
      console.error(error);
      message.error('Failed to create blog post.');
      // alert('Failed to create blog post.');
    }
  };


  return (
    <>
      <Row className="m-2" align="middle">
        <Col span={16}>
          <h2 className="text-2xl font-bold">Add Blog</h2>
        </Col>
        <Col span={8} className="d-flex justify-end">
          <Link to={"/backend/blog"}>
            <button className="d-flex justify-content-center align-items-center w-full border btn-brand">
              <FaArrowLeft className="me-2" />
              <div className="ms-2">Back</div>
            </button>
          </Link>
        </Col>
      </Row>

      <div className="flex justify-center min-h-screen items-center bg-gray-100">
        <div className="bg-white p-6 rounded-lg shadow-lg w-full">
          <Form
            form={form}
            name="dynamic_form_nest_item"
            onFinish={onFinish}
            autoComplete="off"
            initialValues={{ content: [{ section: '', description: '', image: [] }] }}
          >
            <Row gutter={24}>
              <Col xs={24} sm={12} md={12}>
                <label className='font-bold pb-2'>Title</label>
                <Form.Item
                  name={'title'}
                  rules={[{ required: true, message: 'Missing Title' }]}
                >
                  <Input size="large" className="rounded border border-1" />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={12}>
                <label className='font-bold pb-2'>Description</label>
                <Form.Item
                  name={'description'}
                  rules={[{ required: true, message: 'Missing Description' }]}
                >
                  <Input.TextArea placeholder="Description" />
                </Form.Item>
              </Col>
            </Row>

            <Form.List name="content">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, fieldKey, ...restField }) => (
                    <div key={key} className="bg-gray-50 p-4 rounded-lg shadow-sm space-y-4">
                      <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={7}>
                          <label className='font-bold pb-2'>Heading</label>
                          <Form.Item
                            {...restField}
                            name={[name, 'section']}
                            rules={[{ required: true, message: 'Missing heading' }]}
                          >
                            <Input.TextArea placeholder="Heading" />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={7}>
                          <label className='font-bold pb-2'>Image</label>
                          <Form.Item
                            {...restField}
                            name={[name, 'image']}
                            valuePropName="fileList"
                            getValueFromEvent={(e) => Array.isArray(e) ? e : e?.fileList}
                            rules={[{ required: true, message: 'Missing upload' }]}
                          >
                            <Upload
                              listType="picture-card"
                              beforeUpload={() => false}
                              showUploadList={{ showPreviewIcon: true }}
                            >
                              <UploadOutlined />
                            </Upload>
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={7}>
                          <label className='font-bold pb-2'>Description</label>
                          <Form.Item
                            {...restField}
                            name={[name, 'description']}
                            rules={[{ required: true, message: 'Missing description' }]}
                          >
                            <ReactQuill theme="snow" className="rounded border border-1" style={{ height: '150px' }} />
                          </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={3} className="flex justify-end items-center">
                          <Tooltip title="Remove this field">
                            <MinusCircleOutlined onClick={() => remove(name)} className="text-red-500 text-xl cursor-pointer" />
                          </Tooltip>
                        </Col>
                      </Row>
                    </div>
                  ))}
                  <Form.Item>
                    <Button type="dashed" className='mt-4' onClick={() => add()} block icon={<PlusOutlined />}>
                      Add field
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
            <Form.Item>
              <button type="submit" className="btn-brand">
                Submit
              </button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </>
  );
};

export default AddBlog;